import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import ProductCard from './components/ProductCard';
import Button from './components/Button';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 10000]);

    const categories = ['All', 'Clothing', 'Accessories', 'Footwear', 'Bags'];
    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest' },
    ];

    const products = [
        {
            id: 1,
            name: 'Minimalist Tote Bag',
            category: 'Bags',
            price: 2499,
            originalPrice: 3499,
            image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600',
        },
        {
            id: 2,
            name: 'Classic White Shirt',
            category: 'Clothing',
            price: 1999,
            image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600',
        },
        {
            id: 3,
            name: 'Leather Wallet',
            category: 'Accessories',
            price: 1499,
            originalPrice: 1999,
            image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?w=600',
        },
        {
            id: 4,
            name: 'Black Sneakers',
            category: 'Footwear',
            price: 3999,
            image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600',
        },
        {
            id: 5,
            name: 'Cotton T-Shirt',
            category: 'Clothing',
            price: 899,
            image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600',
        },
        {
            id: 6,
            name: 'Sunglasses',
            category: 'Accessories',
            price: 1899,
            image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600',
        },
        {
            id: 7,
            name: 'Denim Jacket',
            category: 'Clothing',
            price: 3499,
            image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=600',
        },
        {
            id: 8,
            name: 'Leather Boots',
            category: 'Footwear',
            price: 4999,
            originalPrice: 5999,
            image: 'https://images.unsplash.com/photo-1638247025967-b4e38f787b76?w=600',
        },
        {
            id: 9,
            name: 'Canvas Backpack',
            category: 'Bags',
            price: 2299,
            image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600',
        },
        {
            id: 10,
            name: 'Wool Scarf',
            category: 'Accessories',
            price: 1299,
            image: 'https://images.unsplash.com/photo-1520903920243-00d872a2d1c9?w=600',
        },
        {
            id: 11,
            name: 'Slim Fit Jeans',
            category: 'Clothing',
            price: 2499,
            image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600',
        },
        {
            id: 12,
            name: 'Crossbody Bag',
            category: 'Bags',
            price: 1999,
            image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600',
        },
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-white">
            <Header cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                {/* Page Header - STUDIO style */}
                <div className="mb-8 lg:mb-12">
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">Shop</h1>
                    <p className="text-gray-600">Discover our curated collection of minimalist essentials</p>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col md:flex-row gap-4 mb-8">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search products..."
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                        />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    {/* Filter Toggle */}
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <SlidersHorizontal size={20} className="mr-2" />
                        Filters
                    </Button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-gray-50 border border-gray-200 p-6 mb-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-semibold text-lg text-gray-900">Filters</h3>
                            <button onClick={() => setShowFilters(false)}>
                                <X size={20} className="text-gray-600 hover:text-gray-900" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Price Range */}
                            <div>
                                <h4 className="font-medium mb-3 text-gray-900">Price Range</h4>
                                <div className="space-y-2">
                                    <input
                                        type="range"
                                        min="0"
                                        max="10000"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>₹0</span>
                                        <span>₹{priceRange[1]}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Size */}
                            <div>
                                <h4 className="font-medium mb-3 text-gray-900">Size</h4>
                                <div className="flex flex-wrap gap-2">
                                    {['XS', 'S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            className="px-4 py-2 border border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors"
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Color */}
                            <div>
                                <h4 className="font-medium mb-3 text-gray-900">Color</h4>
                                <div className="flex flex-wrap gap-3">
                                    {['#000000', '#FFFFFF', '#808080', '#C0C0C0'].map(color => (
                                        <button
                                            key={color}
                                            className="w-10 h-10 border-2 border-gray-300 hover:border-gray-900 transition-colors"
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Tabs */}
                <div className="flex flex-wrap gap-3 mb-8">
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category.toLowerCase())}
                            className={`px-6 py-2 text-sm font-medium transition-colors ${
                                selectedCategory === category.toLowerCase()
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>

                {/* Load More */}
                <div className="text-center mt-12">
                    <Button variant="outline" size="lg">
                        Load More Products
                    </Button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Shop;
